import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../../src/lib/supabase';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { normalizeMesReferencia } from '../../FinanceiroLayout';
import { derivarStatusBoleto } from '../lib/derivarStatusBoleto';
import type { FaturaPainel, FaturaPainelRaw } from '../types';

const COLUNAS =
  'id,cliente_nome,cliente_cpf_cnpj,uc_codigo,mes_referencia,valor_boleto_vigor,valor_pagamento,' +
  'status_boleto_vigor,status_fatura,vencimento_boleto,data_vencimento,data_pgto_boleto,quitado_em,' +
  'cancelado_em,parcelamento_descricao,unidade_geradora_1,empresa_parceira_id,' +
  'energia_injetada_gd1_kwh,energia_injetada_gd2_kwh,valor_emissao_boleto_unico,' +
  'url_boleto,url_boleto_combinado,pdf_boleto_combinado,' +
  // Fatura de UC de agrupamento não vence pela própria data (#6284) — ver `derivarStatusBoleto`.
  'cobranca_agrupada';

const INTERVALO_AUTO_REFRESH_MS = 10 * 60 * 1000; // 10 min — dentro da janela "5-15 min" da spec, seção 9

async function buscarTodasAsFaturas(empresaParceiraId?: string | null): Promise<FaturaPainelRaw[]> {
  let todas: FaturaPainelRaw[] = [];
  let inicio = 0;
  const tamanhoPagina = 1000;
  while (true) {
    let query = supabase
      .from('faturas')
      .select(COLUNAS)
      .eq('ambiente', 'producao')
      // Cliente próprio da parceira (boleto avulso da Gran): a Vigor não recebe nada dele.
      .eq('fora_parceria_vigor', false)
      .neq('status_fatura', 'Excluído')
      // Sem ordem fixa o Postgres não garante a mesma sequência entre uma
      // página e outra: `.range` sozinho pode pular ou repetir faturas.
      .order('id', { ascending: true })
      .range(inicio, inicio + tamanhoPagina - 1);
    if (empresaParceiraId) query = query.eq('empresa_parceira_id', empresaParceiraId);

    const { data, error } = await query;
    if (error) throw error;
    if (!data || data.length === 0) break;
    todas = todas.concat(data as unknown as FaturaPainelRaw[]);
    if (data.length < tamanhoPagina) break;
    inicio += tamanhoPagina;
  }
  return todas;
}

export function usePainelExecutivoData() {
  const { profile } = useAuth();
  const [dados, setDados] = useState<FaturaPainel[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const brutas = await buscarTodasAsFaturas(profile?.empresa_parceira_id);
      const agora = new Date();
      const comStatus: FaturaPainel[] = brutas.map((f) => ({
        ...f,
        mes_referencia: normalizeMesReferencia(f.mes_referencia),
        statusDerivado: derivarStatusBoleto(f, agora),
      }));
      setDados(comStatus);
      setUltimaAtualizacao(agora);
    } catch (e: any) {
      console.error('Erro ao carregar Painel Financeiro Executivo:', e);
      setErro(e?.message || 'Erro ao carregar os dados do painel.');
    } finally {
      setLoading(false);
    }
  }, [profile?.empresa_parceira_id]);

  useEffect(() => {
    carregar();
    intervalRef.current = setInterval(carregar, INTERVALO_AUTO_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [carregar]);

  return { dados, loading, erro, ultimaAtualizacao, refetch: carregar };
}
